import { useState } from "react";

import { toast } from "@/client/hooks/use-toast";
import { useCrBalance } from "@/client/services/credit/balance";
import {
  isPromoCreditExpired,
  PROMO_CREDIT_ADVICE,
  PROMO_CREDIT_EXPIRED,
} from "@/client/services/credit/expiry";
import { useUser } from "@/client/services/user";
import { useDialog } from "@/client/stores/dialog";

import { usePrintResume } from "./print";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  TÉLÉCHARGEMENT PDF — UX "rester dans l'espace client"
 * ─────────────────────────────────────────────────────────────────────────────
 *  L'onglet principal ne quitte JAMAIS l'application :
 *   - pendant la génération : état `loading` sur le bouton (spinner)
 *   - au succès : téléchargement silencieux via ancre programmatique
 *     (le stockage sert les PDF avec `Content-Disposition: attachment`,
 *     donc le navigateur télécharge le fichier SANS ouvrir de nouvel onglet)
 *     + toast de confirmation dans l'app
 *   - à l'erreur : toast d'erreur (affiché par usePrintResume.onError)
 *
 *  Historique : l'ancien flux pré-ouvrait un onglet vide (`window.open("")`)
 *  puis le naviguait vers l'URL du PDF — mais comme le PDF est servi en
 *  pièce jointe, l'onglet ne rendait jamais rien : l'utilisateur restait
 *  bloqué sur une page blanche "about:blank" sans aucun feedback.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * Déclenche le téléchargement depuis l'onglet principal (aucune popup ouverte).
 * Le `Content-Disposition: attachment` renvoyé par le stockage fait le reste :
 * le fichier est téléchargé, la page courante n'est pas remplacée.
 */
export const triggerPdfDownload = (url: string) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

export const useResumeDownload = () => {
  const { user } = useUser();
  const { credit } = useCrBalance(user?.id);
  const { printResume, loading: printLoading } = usePrintResume();
  const { open: openForfait } = useDialog("forfait");
  const [localLoading, setLocalLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [finalUrl, setFinalUrl] = useState<string | undefined>(undefined);

  const balance = credit?.credit ?? 0;
  const canDownload = balance > 0;

  const handleDownload = async (resumeId: string) => {
    setHasError(false);
    setFinalUrl(undefined);

    if (!canDownload) {
      if (isPromoCreditExpired(credit)) {
        toast({ variant: "error", title: PROMO_CREDIT_EXPIRED, description: PROMO_CREDIT_ADVICE });
      }
      localStorage.setItem("to_print", resumeId);
      openForfait("create");
      return;
    }

    setLocalLoading(true);

    try {
      const { url } = await printResume({ id: resumeId });

      setFinalUrl(url);

      // Téléchargement silencieux dans l'onglet principal + confirmation.
      // L'utilisateur reste dans son espace : plus de page blanche.
      triggerPdfDownload(url);

      toast.success("Téléchargement réussi 🎉", {
        description: "Votre CV est disponible dans vos téléchargements.",
        duration: 5_000,
      });
    } catch {
      // Le toast d'erreur est déjà affiché par usePrintResume.onError.
      setHasError(true);
    } finally {
      setLocalLoading(false);
    }
  };

  return {
    balance,
    canDownload,
    loading: printLoading || localLoading,
    handleDownload,
    handleBuyCredits: () => openForfait("create"),
    hasError,
    finalUrl,
    reset: () => {
      setHasError(false);
      setFinalUrl(undefined);
    },
  };
};
